import React, { createContext, use, useRef, useState } from 'react'

import { AxiosError } from 'axios'
import { useToast } from '@chakra-ui/react'

import { convertToMp3IfVideo } from '@/helpers/video-to-audio'
import { useInterview } from '@/react-query/interview/interview'
import {
  useCancelTranscription,
  useUploadAndStartTranscription,
} from '@/react-query/transcription/transcription'
import { InterviewDataContext } from '@/views/screens/Interview/context'

interface InputTranscriptContextType {
  cancelTranscription: () => Promise<void>
  isCancellingTranscription: boolean
  startFileTranscription: ({
    file,
    source,
    interviewId,
  }: {
    file: File
    source: 'localUpload' | 'micRecording' | 'tabRecording'
    interviewId: string
  }) => Promise<void>
}

export const InputTranscriptContext = createContext<InputTranscriptContextType>(
  {} as InputTranscriptContextType,
)

export const InputTranscriptProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const {
    setFile,
    setFileUploadProgress,
    setIsFileUploading,
    isFileUploading,
  } = use(InterviewDataContext)

  const [isCancellingTranscription, setIsCancellingTranscription] =
    useState(false)

  const interview = useInterview().data

  const uploadAbortController = useRef<AbortController | null>(null)

  const cancelTranscriptionMutation = useCancelTranscription()
  const toast = useToast()

  const uploadAndStartTranscriptionMutation = useUploadAndStartTranscription(
    setFileUploadProgress,
    setIsFileUploading,
    uploadAbortController,
  )

  const cancelTranscription = async () => {
    setIsCancellingTranscription(true)
    await cancelTranscriptionMutation.mutateAsync()
    uploadAbortController.current?.abort()
    setFile(undefined)
    setFileUploadProgress(0)
    setIsFileUploading(false)
    uploadAbortController.current = null

    if (!interview) {
      return
    }
    setIsCancellingTranscription(false)
  }

  const startFileTranscription = async ({
    file,
    source,
    interviewId,
  }: {
    file: File
    source: 'localUpload' | 'micRecording' | 'tabRecording'
    interviewId: string
  }) => {
    if (isFileUploading) {
      return
    }
    setIsFileUploading(true)
    setFileUploadProgress(0)

    uploadAbortController.current = new AbortController()
    try {
      const inputToTranscribe = await convertToMp3IfVideo(file)
      const fileToSave = new File([inputToTranscribe], file.name, {
        type: inputToTranscribe.type,
      })
      setFile(fileToSave)
      await uploadAndStartTranscriptionMutation.mutateAsync({
        file: fileToSave,
        interviewId,
        source,
      })
    } catch (error) {
      console.log(error)
      if (
        error instanceof AxiosError &&
        error.response?.status === 400 &&
        error.response?.data.message ===
          'Audio is probably silent, or error converting to audio'
      ) {
        toast({
          title: 'Silent audio detected',
          description: 'Please try again with a different file',
          status: 'error',
          isClosable: true,
        })
      }
    }
    setIsFileUploading(false)
  }

  return (
    <InputTranscriptContext
      value={{
        cancelTranscription,
        isCancellingTranscription,
        startFileTranscription,
      }}
    >
      {children}
    </InputTranscriptContext>
  )
}
